import { MenuItem } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { IndexItem } from "IndexItem";
import { ReactComponent as CheckIcon } from "components/Icons/check.svg";
import { ReactComponent as ChevronLeftIcon } from "components/Icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "components/Icons/chevron-right.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useDebounce } from "hooks/useDebounce";
import { kebabCase } from "lodash";
import { useState } from "react";
import { Search } from "./Search";
import {
  CategorizedItems,
  categorizeItems,
  filterElements,
  isValueSelected,
} from "./utils";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    header: {
      backgroundColor: theme.gray50,
      padding: theme.spacing(4),
      display: "flex",
      alignItems: "center",
      borderBottom: `1px solid ${theme.gray100}`,
      cursor: "pointer",
    },
    headerTitle: {
      fontSize: "14px",
      fontWeight: 500,
      marginLeft: theme.spacing(2),
      lineHeight: 0,
    },
    categoryWrapper: {
      display: "flex",
      gap: theme.spacing(6),
      justifyContent: "space-between",
    },
    icon: {
      widht: "20px",
      height: "20px",
    },
    selected: {
      backgroundColor: "#5c4baa14",
    },
    noOption: {
      color: theme.gray400,
    },
    checkbox: {
      border: `3px solid ${theme.gray500}`,
      width: "18px",
      height: "18px",
      minWidth: "18px",
      borderRadius: "4px",
      marginRight: theme.spacing(8),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    checkboxChecked: {
      border: `3px solid ${theme.primary500}`,
      backgroundColor: theme.primary500,
    },
    labelWrapper: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      overflow: "hidden",
    },
    label: {
      textOverflow: "ellipsis",
      overflow: "hidden",
      marginRight: "2rem",
    },
    indexItem: { marginLeft: "auto" },
  }),
);

export type Item = {
  id: string | undefined;
  label: string;
  labelInfo?: string;
  category?: string;
  disabled?: boolean;
  isDeprecated?: boolean;
};

interface Props {
  items: Item[];
  setValue: (value: string | number | Item | undefined) => void;
  value?: string[];
  displaySearch?: boolean;
  searchedValue: string;
  setSearchedValue: (val: string) => void;
  isTypingGlobal: boolean;
  "data-test-id"?: string;
  keepDetailedValue?: boolean;
  multiple?: boolean;
}

export const Categories = ({
  items,
  setValue,
  value,
  displaySearch,
  setSearchedValue,
  searchedValue,
  isTypingGlobal,
  "data-test-id": dataTestId,
  keepDetailedValue,
  multiple,
}: Props) => {
  const [category, setCategory] = useState<string | null>(null);
  const [searchedInGroupValue, setSearchedGroupValue] = useState("");
  const classes = useStyles();
  const { debouncedValue: searchQuery, isTyping } =
    useDebounce(searchedInGroupValue);
  const { message } = useMessage();
  const categorizedItems = categorizeItems(items);
  const categoriesName = Object.keys(categorizedItems);

  const options = category
    ? filterElements(categorizedItems[category], searchQuery)
    : [];

  const isAnyOption = category ? options.length > 0 : items.length > 0;

  return (
    <div>
      {category ? (
        <div>
          <div
            className={classes.header}
            onClick={() => {
              setSearchedGroupValue("");
              setCategory(null);
            }}
          >
            <ChevronLeftIcon />
            <span className={classes.headerTitle}>
              {message({ id: "back" })}
            </span>
          </div>
          {displaySearch && (
            <Search
              searchedValue={searchedInGroupValue}
              setSearchedValue={setSearchedGroupValue}
              isLoading={isTyping}
              data-test-id={dataTestId}
            />
          )}
          <div>
            {isAnyOption ? (
              (options as Item[]).map((item: Item) => {
                const isSelected = isValueSelected(item, value);

                return (
                  <MenuItem
                    key={item.id}
                    className={isSelected ? classes.selected : ""}
                    onClick={() => {
                      setValue(keepDetailedValue ? item : item.id);
                      setSearchedValue("");
                    }}
                  >
                    {multiple && item.id && (
                      <div
                        className={`${classes.checkbox} ${
                          isSelected && classes.checkboxChecked
                        }`}
                        data-test-id={kebabCase(item.label)}
                      >
                        {isSelected && <CheckIcon color="white" />}
                      </div>
                    )}
                    <div className={classes.labelWrapper}>
                      <span className={classes.label}>{item.label}</span>
                    </div>
                  </MenuItem>
                );
              })
            ) : (
              <MenuItem className={classes.noOption}>
                {message({ id: "noOptions" })}
              </MenuItem>
            )}
          </div>
        </div>
      ) : (
        <>
          {displaySearch && (
            <Search
              searchedValue={searchedValue}
              setSearchedValue={setSearchedValue}
              isLoading={isTypingGlobal}
              data-test-id={dataTestId}
            />
          )}

          {isAnyOption ? (
            categoriesName.map((category: string) => {
              const isUnCategorized = category === "unCategorized";

              if (isUnCategorized) {
                return categorizedItems["unCategorized"].map((item: Item) => {
                  const isSelected = isValueSelected(item, value);
                  return (
                    <MenuItem
                      data-test-id={kebabCase(item.label)}
                      className={isSelected ? classes.selected : ""}
                      key={item.id}
                      onClick={() =>
                        setValue(keepDetailedValue ? item : item.id)
                      }
                    >
                      {multiple && item.id && (
                        <div
                          data-test-id={kebabCase(item.label)}
                          className={`${classes.checkbox} ${
                            isSelected && classes.checkboxChecked
                          }`}
                        >
                          {isSelected && <CheckIcon color="white" />}
                        </div>
                      )}
                      <div
                        data-test-id={kebabCase(item.label)}
                        className={classes.labelWrapper}
                      >
                        <span className={classes.label}>{item.label}</span>
                      </div>
                    </MenuItem>
                  );
                });
              }

              const countOccurrencesByKey = (
                obj: CategorizedItems,
                ids?: string[],
              ) => {
                const result: {
                  [key: string]: number;
                } = {};

                // Iterate over keys of the object
                Object.keys(obj).forEach((key: string) => {
                  result[key] = obj[key]?.filter(
                    ({ id }) => id && ids?.includes(id),
                  ).length;
                });

                return result;
              };

              const selectedItemsInCategory = countOccurrencesByKey(
                categorizedItems,
                value,
              );

              return (
                <MenuItem
                  key={category}
                  onClick={() => {
                    setCategory(category);
                  }}
                  data-test-id={category}
                  className={classes.categoryWrapper}
                >
                  <span>{message({ id: category })}</span>
                  {selectedItemsInCategory[category] > 0 && (
                    <IndexItem className={classes.indexItem}>
                      {selectedItemsInCategory[category]}
                    </IndexItem>
                  )}
                  <ChevronRightIcon className={classes.icon} />
                </MenuItem>
              );
            })
          ) : (
            <MenuItem className={classes.noOption}>
              {message({ id: "noOptions" })}
            </MenuItem>
          )}
        </>
      )}
    </div>
  );
};
